import { isHoldExpired } from '../policies';
import { preflightDue } from '../reminders';
import { toMs } from '../time';
import { downgradeIfTrialExpired } from '../trial';
import { isTemplateId } from '../../adapters/messaging/templates';
import type { Appointment, Business, Id } from '../types';
import { loadBundle, notifyAppointment, type Deps } from './context';
import type { Bundle } from './context';

export interface SchedulerReport {
  at: string;
  downgradedTrials: number;
  /** Accounts hard-deleted because their grace period ended. */
  accountsDeleted: number;
  depositsConfirmed: number;
  holdsReleased: number;
  offersExpired: number;
  suppressed: number;
  deferred: number;
  progressed: number;
}

/** The periodic job: app start, 30 s tick and every clock advance call this. Idempotent. */
export function maintenanceUseCases(d: Deps, expireWaitlist: (b: Bundle) => Promise<number>, purgeDueAccounts: () => Promise<number> = async () => 0) {
  async function reconcileDeposits(b: Bundle): Promise<{ confirmed: number; released: number }> {
    const nowMs = d.clock.now();
    const holds = await d.storage.repo('appointments').list({ where: { businessId: b.business.id }, filter: (a) => a.status === 'awaiting_deposit' });
    let confirmed = 0, released = 0;
    for (const a of holds) {
      const payment = a.paymentId ? await d.payments.getStatus(a.paymentId) : undefined;
      const paid = payment?.status === 'paid';
      const dead = payment?.status === 'failed' || payment?.status === 'expired' || isHoldExpired(a, nowMs);
      if (!paid && !dead) continue;
      const t = d.clock.nowIso();
      const next = await d.storage.transact(['appointments'], async (tx) => {
        const cur = await tx.get('appointments', a.id);
        if (!cur || cur.status !== 'awaiting_deposit') return undefined;
        const { holdExpiresAt: _h, ...rest } = cur;
        const row: Appointment = paid
          ? { ...rest, status: b.settings.approvalMode === 'manual' ? 'pending' : 'confirmed', depositStatus: 'paid', updatedAt: t }
          : { ...rest, status: 'cancelled_client', cancelReason: payment?.status === 'failed' ? 'deposit_failed' : 'deposit_timeout', depositStatus: payment?.status === 'failed' ? 'failed' : 'expired', cancelledAt: t, updatedAt: t };
        await tx.put('appointments', row);
        return row;
      });
      if (!next) continue;
      if (paid) { confirmed++; await notifyAppointment(d, 'deposit_paid', next); continue; }
      released++;
      if (payment?.status === 'pending') await d.payments.complete(payment.id, 'timeout');
      await notifyAppointment(d, 'deposit_expired', next);
    }
    return { confirmed, released };
  }

  /** Re-checks queued messages that are now due: consent may be gone, quiet hours may apply. */
  async function preflightMessages(b: Bundle): Promise<{ suppressed: number; deferred: number }> {
    const nowMs = d.clock.now();
    const due = await d.storage.repo('messages').list({ where: { businessId: b.business.id, status: 'queued' }, filter: (m) => toMs(m.scheduledFor) <= nowMs });
    if (due.length === 0) return { suppressed: 0, deferred: 0 };
    const consents = await d.storage.repo('consents').list({ where: { businessId: b.business.id } });
    let suppressed = 0, deferred = 0;
    for (const m of due) {
      if (!isTemplateId(m.template) || !m.clientId) continue;
      const appt = m.appointmentId ? await d.storage.repo('appointments').get(m.appointmentId) : undefined;
      const verdict = preflightDue(m, {
        template: m.template, clientId: m.clientId, consents, plan: b.plan, settings: b.settings, tz: b.business.tz, nowMs,
        ...(appt ? { appointmentStartMs: toMs(appt.start) } : {}),
      });
      if (verdict.action === 'suppress') { await d.messaging.markSuppressed(m.id, verdict.reason); suppressed++; }
      else if (verdict.action === 'defer') { await d.messaging.reschedule(m.id, verdict.scheduledFor, 'quiet_hours_deferred'); deferred++; }
    }
    return { suppressed, deferred };
  }

  async function runScheduledJobs(): Promise<SchedulerReport> {
    const report: SchedulerReport = { at: d.clock.nowIso(), downgradedTrials: 0, accountsDeleted: 0, depositsConfirmed: 0, holdsReleased: 0, offersExpired: 0, suppressed: 0, deferred: 0, progressed: 0 };
    report.accountsDeleted = await purgeDueAccounts();
    const businesses: Business[] = await d.storage.repo('businesses').list();
    for (const business of businesses) {
      const downgraded = downgradeIfTrialExpired(business, d.clock.now(), d.clock.nowIso());
      if (downgraded) { await d.storage.repo('businesses').put(downgraded); report.downgradedTrials++; }
    }
    await d.payments.sweepExpired();
    for (const business of businesses) {
      const b = await loadBundle(d, business.id as Id);
      if (!b) continue;
      const deposits = await reconcileDeposits(b);
      report.depositsConfirmed += deposits.confirmed;
      report.holdsReleased += deposits.released;
      report.offersExpired += await expireWaitlist(b);
      const pre = await preflightMessages(b);
      report.suppressed += pre.suppressed;
      report.deferred += pre.deferred;
    }
    report.progressed = await d.messaging.tick();
    return report;
  }

  return { runScheduledJobs };
}
